import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue, useMotionTemplate, useReducedMotion } from "framer-motion";

// Scroll-driven depth: a section tips up from below in perspective, settles flat, then recedes as it leaves.
export const Depth4D = ({ children, className = "", strength = 1, recedeOnly = false }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const p = useSpring(scrollYProgress, { stiffness: 90, damping: 26, mass: 0.6, restDelta: 0.001 });
  const enter = recedeOnly ? 0 : 1;
  const rotateX = useTransform(p, [0, 0.35, 0.65, 1], [9 * strength * enter, 0, 0, -5 * strength]);
  const y = useTransform(p, [0, 0.35, 0.65, 1], [46 * strength * enter, 0, 0, -28 * strength]);
  const scale = useTransform(p, [0, 0.35, 0.65, 1], [1 - 0.045 * enter, 1, 1, 0.985]);
  if (reduce || window.matchMedia("(max-width: 1023px), (pointer: coarse)").matches) return <div className={className}>{children}</div>;
  return (
    <div ref={ref} className={className} style={{ perspective: 1600 }} data-depth4d>
      <motion.div style={{ rotateX, y, scale, transformOrigin: "50% 100%", willChange: "transform" }}>{children}</motion.div>
    </div>
  );
};

// Pointer tilt + soft specular light that follows the cursor (mouse only; touch stays still).
export const useTilt = (max = 7) => {
  const reduce = useReducedMotion();
  const mx = useMotionValue(0.5), my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [max, -max]), { stiffness: 140, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-max, max]), { stiffness: 140, damping: 18 });
  const gx = useTransform(mx, (v) => `${v * 100}%`), gy = useTransform(my, (v) => `${v * 100}%`);
  const glare = useMotionTemplate`radial-gradient(420px circle at ${gx} ${gy}, rgba(246,227,166,0.10), transparent 60%)`;
  const onPointerMove = (e) => {
    if (reduce || e.pointerType !== "mouse") return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width); my.set((e.clientY - r.top) / r.height);
  };
  const onPointerLeave = () => { mx.set(0.5); my.set(0.5); };
  return { style: { rotateX, rotateY, transformPerspective: 1100, willChange: "transform", backfaceVisibility: "hidden", outline: "1px solid transparent" }, handlers: { onPointerMove, onPointerLeave }, glare };
};

export const Glare = ({ glare }) => (
  <motion.span aria-hidden className="pointer-events-none absolute inset-0 z-[1] opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: glare }} />
);

export default Depth4D;
